import debugConsole from './debugConsole';
import { leftPad } from './stringPad';
import {
  PAYLOAD_DATE_FORMAT,
  PAYMENT_BIWEEKLY_CADENCES,
  PAYMENT_FREQUENCIES,
  PAYMENT_PLAN_TYPES,
} from '../constants';

export const formatDateWithString = (date, format = PAYLOAD_DATE_FORMAT) => {
  date = new Date(date);
  
  const y = date.getFullYear().toString();
  const m = (date.getMonth() + 1).toString();
  const d = date.getDate().toString();

  const yy = leftPad(y, 2);
  const yyyy = leftPad(yy, 2);
  const mm = leftPad(m, 2);
  const dd = leftPad(d, 2);

  return format
    .replace(/YYYY/g, yyyy)
    .replace(/YY/g, yy)
    .replace(/Y/g, y)
    .replace(/MM/g, mm)
    .replace(/M/g, m)
    .replace(/DD/g, dd)
    .replace(/D/g, d);
};

export const formatApiCalendarDate = (
  dateValue,
  locale = undefined,
  options = { month: 'numeric', day: 'numeric', year: 'numeric' }
) => {
  if (!dateValue) return '';

  const isoCalendarMatch =
    typeof dateValue === 'string'
      ? dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/)
      : null;

  const date = isoCalendarMatch
    ? new Date(
        Number(isoCalendarMatch[1]),
        Number(isoCalendarMatch[2]) - 1,
        Number(isoCalendarMatch[3])
      )
    : new Date(dateValue);

  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(locale, options).format(date);
};

export const MS_PER_DAY = 86400000;

export const daysUntil = date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  date = new Date(date);
  date.setHours(0, 0, 0, 0);

  return Math.max(Math.floor((date - today) / MS_PER_DAY), 0);
};

export const getApiCalendarDate = (dateValue) => {
  if (!dateValue) return null;

  const isoCalendarMatch =
    typeof dateValue === 'string'
      ? dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/)
      : null;

  const date = isoCalendarMatch
    ? new Date(
        Number(isoCalendarMatch[1]),
        Number(isoCalendarMatch[2]) - 1,
        Number(isoCalendarMatch[3])
      )
    : new Date(dateValue);

  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

export const getPaymentStartDateLimitFromExpiry = (
  expiryDate,
  { defaultLimit = 14, today = new Date() } = {}
) => {
  const expiryCalendarDate = getApiCalendarDate(expiryDate);
  if (!expiryCalendarDate) return defaultLimit;

  const currentDate = new Date(today);
  currentDate.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((expiryCalendarDate - currentDate) / MS_PER_DAY));
};

export const getMonthAbbreviation = (m = new Date(), locale = undefined) => {
  const date = m instanceof Date
    ? m
    : new Date(0, m - 1, 0, 0, 0, 0, 0);
  
  return date.toLocaleDateString(locale, { month: 'short' });
};

export const numDaysInMonth = (month = new Date(), year) => {
  const date = month instanceof Date
    ? new Date(month)
    : new Date(year || new Date().getFullYear(), month + 1, 1);
  
  date.setDate(0);

  return date.getDate();
};

export const weekdayString = (dayNumber, locale = undefined) => {
  // first of the month but also Monday; date and day are both 1
  const date = new Date('5/1/2023');
  // which lets us do this
  date.setDate(dayNumber);

  // take just the weekday as a string
  return date.toLocaleDateString(locale, { weekday: 'long' });
};

const BIWEEKLY_CADENCE_DAYS = {
  [PAYMENT_BIWEEKLY_CADENCES.firstAndFifteenth]: [1, 15],
  [PAYMENT_BIWEEKLY_CADENCES.fifteenthAndLastDay]: [15, 'last'],
};

const getSemiMonthlyDay = (year, month, day) =>
  day === 'last' ? numDaysInMonth(month, year) : day;

const getSemiMonthlyDatesForMonth = (year, month, cadence) => {
  const cadenceDays = BIWEEKLY_CADENCE_DAYS[cadence];
  if (!cadenceDays) return [];

  return cadenceDays.map((day) =>
    new Date(year, month, getSemiMonthlyDay(year, month, day))
  );
};

export const getNextBiweeklyCadenceDate = (
  cadence = PAYMENT_BIWEEKLY_CADENCES.firstAndFifteenth,
  fromDate = new Date()
) => {
  const startDate = new Date(fromDate);
  startDate.setHours(0, 0, 0, 0);

  for (let monthOffset = 0; monthOffset < 2; monthOffset += 1) {
    const month = startDate.getMonth() + monthOffset;
    const year = startDate.getFullYear() + Math.floor(month / 12);
    const normalizedMonth = month % 12;
    const nextDate = getSemiMonthlyDatesForMonth(year, normalizedMonth, cadence)
      .find((candidate) => candidate >= startDate);

    if (nextDate) return nextDate;
  }

  return startDate;
};

const getNextMonthlyCadenceDate = (paymentDate, paymentDayOfMonth) => {
  if (!paymentDayOfMonth) return paymentDate;

  // Candidates are midnight, but the start date can carry a time of day
  // (generatePaymentScheduleFromPaymentPlan shifts local midnight by the UTC
  // offset), which would skip the chosen start day and start a month later.
  const startDate = new Date(paymentDate);
  startDate.setHours(0, 0, 0, 0);

  const initialMonth = startDate.getMonth();
  const initialYear = startDate.getFullYear();

  for (let monthOffset = 0; monthOffset < 2; monthOffset += 1) {
    const month = initialMonth + monthOffset;
    const year = initialYear + Math.floor(month / 12);
    const normalizedMonth = month % 12;
    const day = Math.min(paymentDayOfMonth, numDaysInMonth(normalizedMonth, year));
    const candidate = new Date(year, normalizedMonth, day);

    if (candidate >= startDate) return candidate;
  }

  return startDate;
};

const generateBiweeklyCadenceSchedule = ({
  cadence,
  numPayments,
  paymentStartDate,
  standardPaymentAmount,
}) => {
  const startDate = new Date(paymentStartDate);
  startDate.setHours(0, 0, 0, 0);

  const schedule = [];
  let monthOffset = 0;

  while (schedule.length < numPayments) {
    const month = startDate.getMonth() + monthOffset;
    const year = startDate.getFullYear() + Math.floor(month / 12);
    const normalizedMonth = month % 12;
    const monthDates = getSemiMonthlyDatesForMonth(year, normalizedMonth, cadence);

    monthDates.forEach((candidate) => {
      if (schedule.length < numPayments && candidate >= startDate) {
        schedule.push({
          amount: parseFloat(standardPaymentAmount),
          date: formatDateWithString(candidate),
        });
      }
    });

    monthOffset += 1;
  }

  return schedule;
};

export const generatePaymentScheduleFromPaymentPlan = ({
  numPayments,
  paymentFrequency,
  paymentBiweeklyCadence,
  paymentDayOfMonth,
  paymentStartDate,
  paymentDayOfWeek,
  paymentSchedule,
  planType,
  standardPaymentAmount,
  lastPaymentAmount
}) => {
  let paymentDate = new Date(paymentStartDate);
  paymentDate.setMinutes(paymentDate.getMinutes() + paymentDate.getTimezoneOffset());

  const finalPaymentSchedule = [];

  if (paymentSchedule?.length > 0) {
    return paymentSchedule.map(
      ({ date, amount }) => {
        return { amount: Math.round(amount * 100) / 100, date: typeof date === 'string' ? date : formatDateWithString(date) };
      }
    );
  } else if (planType === PAYMENT_PLAN_TYPES.paymentPlan || planType === PAYMENT_PLAN_TYPES.settlementPlan) {
    switch (paymentFrequency) {
      case PAYMENT_FREQUENCIES.monthly:
        const firstPaymentDate = getNextMonthlyCadenceDate(
          paymentDate,
          paymentDayOfMonth
        );
        const initialDay = paymentDayOfMonth || firstPaymentDate.getDate();
        const initialMonth = firstPaymentDate.getMonth();
        const initialYear = firstPaymentDate.getFullYear();

        for (let i = 0; i < numPayments; ++i) {
          const monthOffset = initialMonth + i;
          const month = monthOffset % 12;
          const year = initialYear + Math.floor(monthOffset / 12);
          const day = Math.min(initialDay, numDaysInMonth(month, year));

          finalPaymentSchedule.push({
            amount: parseFloat(standardPaymentAmount),
            date: formatDateWithString(new Date(year, month, day))
          });
        }

        break;
      
      case PAYMENT_FREQUENCIES.weekly:
      case PAYMENT_FREQUENCIES.everyTwoWeeks:
      case PAYMENT_FREQUENCIES.biweekly:
        if (
          paymentFrequency === PAYMENT_FREQUENCIES.biweekly &&
          BIWEEKLY_CADENCE_DAYS[paymentBiweeklyCadence]
        ) {
          finalPaymentSchedule.push(...generateBiweeklyCadenceSchedule({
            cadence: paymentBiweeklyCadence,
            numPayments,
            paymentStartDate: paymentDate,
            standardPaymentAmount,
          }));
          break;
        }

        if (paymentDate.getDay() !== paymentDayOfWeek) {
          paymentDate = new Date();
          paymentDate.setDate(paymentDate.getDate() + (7 + paymentDayOfWeek - paymentDate.getDay()) % 7);
        }
        
        const intervalDays = paymentFrequency === PAYMENT_FREQUENCIES.weekly
          ? 7
          : 14;

        for (let i = 0; i < numPayments; ++i) {
          finalPaymentSchedule.push({
            amount: parseFloat(standardPaymentAmount),
            date: formatDateWithString(paymentDate)
          });

          paymentDate.setDate(paymentDate.getDate() + intervalDays);
        }
        
        break;
      
      default:
        debugConsole.error(`Failed to generate payment schedule: unknown paymentFrequency ${paymentFrequency}`);
        return finalPaymentSchedule;
    }

    if (lastPaymentAmount && finalPaymentSchedule.length > 0) {
      finalPaymentSchedule[finalPaymentSchedule.length - 1].amount = parseFloat(lastPaymentAmount);
    }

    return finalPaymentSchedule;
  } else {
    // single payment
    return [{ amount: parseFloat(standardPaymentAmount), date: formatDateWithString(paymentDate) }];
  }
};
